import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  ExternalLink,
  Info,
  Lock,
  MapPin,
  MessageCircle,
  MessageSquare,
  Navigation,
  Package,
  Phone,
  Store,
  User,
  X
} from 'lucide-react';
import { ChatModal, EmergencyAlertModal } from '../deliverydriver/modals';

function normalizePeruPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 9) return '';
  return `51${digits.slice(-9)}`;
}

function getDestination(delivery) {
  const lat = delivery?.latitude ?? delivery?.provisional_latitude;
  const lng = delivery?.longitude ?? delivery?.provisional_longitude;

  if (lat == null || lng == null) return null;
  return { lat, lng };
}

function DetailLine({ icon: Icon, label, value }) {
  if (!value && value !== 0) return null;

  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase text-gray-500">{label}</div>
        <div className="text-sm font-medium text-gray-900 break-words">{value}</div>
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick, variant = 'default', disabled = false, loading = false }) {
  const styles = {
    default: 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50',
    primary: 'bg-brand-primary border-brand-primary text-white hover:bg-brand-primary/90',
    success: 'bg-brand-green border-brand-green text-white hover:bg-brand-green/90',
    danger: 'bg-red-600 border-red-600 text-white hover:bg-red-700',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        min-h-[48px] rounded-lg border px-3 py-2 text-sm font-semibold
        flex items-center justify-center gap-2 transition-colors
        disabled:opacity-45 disabled:cursor-not-allowed
        ${styles[variant]}
      `}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        <Icon className="w-4 h-4 flex-shrink-0" />
      )}
      <span className="truncate">{label}</span>
    </button>
  );
}

function DeliveryQuickActionsModal({
  open,
  onClose,
  delivery,
  driverId,
}) {
  const [showFullInfo, setShowFullInfo] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);

  const destination = useMemo(() => getDestination(delivery), [delivery]);
  const clientPhone = normalizePeruPhone(delivery?.recipient_phone);
  const providerPhone = normalizePeruPhone(delivery?.user_phone);

  useEffect(() => {
    if (!open) {
      setShowFullInfo(false);
      setShowChatModal(false);
      setShowAlertModal(false);
    }
  }, [open]);

  if (!open || !delivery) return null;

  const openWhatsApp = (target) => {
    const phone = target === 'provider' ? providerPhone : clientPhone;
    if (!phone) {
      alert('No hay telefono disponible para este contacto.');
      return;
    }

    const message = target === 'provider'
      ? `Hola, soy el motorizado de Fast Max. Necesito confirmar datos de la entrega ${delivery.id} para ${delivery.recipient_name}.`
      : `Hola ${delivery.recipient_name}, soy el motorizado de Fast Max. Te escribo para coordinar tu entrega.`;

    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const callPhone = (target) => {
    const phone = target === 'provider' ? providerPhone : clientPhone;
    if (!phone) {
      alert('No hay telefono disponible para este contacto.');
      return;
    }

    window.open(`tel:+${phone}`, '_self');
  };

  const openDirections = () => {
    if (!destination) {
      alert('Esta entrega aun no tiene una ubicacion disponible.');
      return;
    }

    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleAlertSubmit = async ({ alert_type, message }) => {
    if (!delivery?.id || !alert_type) return false;

    try {
      setSendingAlert(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/delivery-alerts`,
        {
          delivery_point_id: delivery.id,
          driver_id: driverId || delivery.driver_id,
          alert_type,
          message,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );

      alert('Alerta enviada en tiempo real.');
      return true;
    } catch (error) {
      const reason = error?.response?.data?.error || error.message;
      alert(`No se pudo enviar la alerta: ${reason}`);
      return false;
    } finally {
      setSendingAlert(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[1900] flex items-end sm:items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div
          className="
            relative w-full sm:max-w-lg max-h-[92dvh] bg-white rounded-t-2xl sm:rounded-2xl
            shadow-2xl overflow-hidden flex flex-col
          "
        >
          <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-amber-700 text-xs font-bold uppercase">
                <Lock className="w-4 h-4" />
                Secuencia bloqueada
              </div>
              <h2 className="text-xl font-bold text-gray-900 mt-1 truncate">
                #{delivery.sequence} {delivery.recipient_name}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Puedes coordinar o reportar, pero la entrega completa sigue bloqueada.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 flex-shrink-0"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="overflow-y-auto px-4 sm:px-5 py-5 space-y-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
              <ActionButton
                icon={MessageCircle}
                label="WhatsApp cliente"
                onClick={() => openWhatsApp('client')}
                variant="success"
                disabled={!clientPhone}
              />
              <ActionButton
                icon={Phone}
                label="Llamar cliente"
                onClick={() => callPhone('client')}
                variant="primary"
                disabled={!clientPhone}
              />
              <ActionButton
                icon={MessageSquare}
                label="Chat entrega"
                onClick={() => setShowChatModal(true)}
                disabled={!delivery?.id}
              />
              <ActionButton
                icon={Navigation}
                label="Google Maps"
                onClick={openDirections}
                disabled={!destination}
              />
              <ActionButton
                icon={MessageCircle}
                label="WhatsApp tienda"
                onClick={() => openWhatsApp('provider')}
                disabled={!providerPhone}
              />
              <ActionButton
                icon={AlertTriangle}
                label="Alertar"
                onClick={() => setShowAlertModal(true)}
                variant="danger"
              />
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Para abrir la pantalla completa de entrega, primero completa las entregas anteriores en secuencia.
            </div>

            <div className="space-y-4">
              <DetailLine icon={MapPin} label="Direccion" value={delivery.address} />
              <DetailLine icon={Package} label="Producto" value={delivery.product} />
              <DetailLine icon={Store} label="Tienda" value={delivery.user_name} />
              <DetailLine icon={User} label="Cliente" value={delivery.recipient_name} />

              {showFullInfo && (
                <>
                  <DetailLine icon={Info} label="Notas" value={delivery.notes} />
                  <DetailLine icon={Package} label="Tracking" value={delivery.tracking_code} />
                  <DetailLine icon={Package} label="Estado paquete" value={delivery.package_status} />
                  <DetailLine icon={Info} label="Cobro" value={delivery.charge ? `S/ ${delivery.charge}` : null} />
                  <DetailLine icon={MapPin} label="Distrito" value={delivery.district} />
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowFullInfo(current => !current)}
              className="w-full min-h-[44px] rounded-lg border border-gray-200 text-gray-800 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-gray-50"
            >
              <ExternalLink className="w-4 h-4" />
              {showFullInfo ? 'Ver menos datos' : 'Ver mas datos'}
            </button>
          </div>
        </div>
      </div>

      <ChatModal
        open={showChatModal}
        onClose={() => setShowChatModal(false)}
        delivery={delivery}
        driverId={driverId || delivery?.driver_id}
      />

      <EmergencyAlertModal
        open={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        onSubmit={handleAlertSubmit}
        loading={sendingAlert}
      />
    </>
  );
}

export default DeliveryQuickActionsModal;
